import Button from '@/components/elements/forms/buttons/Button/Button';
import Icon from '@/components/icons/Icon';
import { _t } from '@/locale';
import { SelectionsBarProps } from './SelectionsBar.types';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Circular edit affordance, shared across all bar states. */
const EditButton = ({ onEdit }: Pick<SelectionsBarProps, 'onEdit'>) => (
  <button
    className="bg-bg_secondary_bold p-xs flex shrink-0 items-center justify-center rounded-full"
    onClick={onEdit}
    type="button"
    aria-label={_t('features.bookingSelection.SelectionsBar.cta.edit')}>
    <Icon variant="edit" size="sm" color="fg_primary" />
  </button>
);

/** Left block: service label + price */
const LeftInfo = ({
  serviceLabel,
  price,
  originalPrice,
}: Pick<SelectionsBarProps, 'serviceLabel' | 'price' | 'originalPrice'>) => {
  const hasCampaignPrice = Boolean(originalPrice && originalPrice !== price);

  return (
    <div className="gap-3xs flex min-w-0 flex-1 flex-col">
      <span className="text-m text-fg_secondary truncate">{serviceLabel}</span>
      {price && <span className="text-fg_primary text-xl font-bold">{price}</span>}
      {price && hasCampaignPrice && (
        <span className="text-s text-fg_secondary truncate line-through">{originalPrice}</span>
      )}
    </div>
  );
};

/** AddOns right slot: pick-employee CTA (cta.pickEmployee) with trailing arrow */
const AddOnsSlot = ({ onPickEmployee }: Pick<SelectionsBarProps, 'onPickEmployee'>) => (
  <Button
    variant="primary"
    size="md"
    label={_t('features.bookingSelection.SelectionsBar.cta.pickEmployee')}
    rightIcon={<Icon variant="arrow-right" size="xs" color="fg_on_primary" />}
    onClick={onPickEmployee}
  />
);

/**
 * SelectEmployee right slot: any-employee CTA with trailing arrow. Same label as the
 * list's own "Valfri utövare" row (one key, so the two can never drift apart).
 */
const AnyEmployeeSlot = ({ onPickAnyEmployee }: Pick<SelectionsBarProps, 'onPickAnyEmployee'>) => (
  <Button
    variant="primary"
    size="md"
    label={_t('features.bookingSelection.anyEmployee')}
    rightIcon={<Icon variant="arrow-right" size="xs" color="fg_on_primary" />}
    onClick={onPickAnyEmployee}
  />
);

/** Search right slot: search-times CTA (cta.searchTimes) with leading search icon */
const SearchSlot = ({ onSearchTimes }: Pick<SelectionsBarProps, 'onSearchTimes'>) => (
  <Button
    variant="primary"
    size="md"
    label={_t('features.bookingSelection.SelectionsBar.cta.searchTimes')}
    leftIcon={<Icon variant="search" size="xs" color="fg_on_primary" />}
    onClick={onSearchTimes}
  />
);

/**
 * The link styling is the affordance, so it is spent only when there is a handler to spend it
 * on: without `onChangeEmployee` there is no other employee to move to, and an underlined
 * link that answers no tap reads as broken rather than as absent.
 */
const employeeLabel = (label: string, onChangeEmployee?: () => void) =>
  onChangeEmployee ? (
    <span className="text-l text-fg_link truncate underline">{label}</span>
  ) : (
    <span className="text-l text-fg_secondary truncate">{label}</span>
  );

/** PickTime right slot: employee name link + avatar */
const PickTimeSlot = ({
  employeeName,
  employeeImgUrl,
  onChangeEmployee,
}: Pick<SelectionsBarProps, 'employeeName' | 'employeeImgUrl' | 'onChangeEmployee'>) => {
  const content = (
    <>
      {employeeLabel(employeeName ?? '', onChangeEmployee)}
      <div className="bg-bg_secondary h-4xl w-4xl flex shrink-0 items-center justify-center overflow-hidden rounded-sm">
        {employeeImgUrl ? (
          <img src={employeeImgUrl} alt={employeeName ?? ''} className="h-full w-full object-contain" />
        ) : (
          <Icon variant="user" color="fg_secondary" size="sm" />
        )}
      </div>
    </>
  );
  const className = 'gap-xs flex w-full min-w-0 items-center justify-end';

  return onChangeEmployee ? (
    <button
      className={`${className} focus-visible:outline-border_focus rounded-sm outline-none focus-visible:outline focus-visible:outline-4`}
      onClick={onChangeEmployee}
      type="button">
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
};

/** PickTimeAnyStaffMember right slot: link text only, no avatar */
const AnyStaffSlot = ({ onChangeEmployee }: Pick<SelectionsBarProps, 'onChangeEmployee'>) => {
  const label = _t('features.bookingSelection.anyEmployee');

  return onChangeEmployee ? (
    <button className="shrink-0" onClick={onChangeEmployee} type="button">
      {employeeLabel(label, onChangeEmployee)}
    </button>
  ) : (
    <div className="shrink-0">{employeeLabel(label, onChangeEmployee)}</div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Presentational, props-driven, no Redux. Shows the current service/price
 * summary and a state-appropriate right-side action. Designed for the
 * mobile booking flow.
 *
 * Widget-safe: no Redux, no router dependencies.
 */
const SelectionsBar = (props: SelectionsBarProps) => {
  const { state, serviceLabel, price, originalPrice, onEdit } = props;

  const rightSlot = (() => {
    switch (state) {
      case 'AddOns':
        return <AddOnsSlot onPickEmployee={props.onPickEmployee} />;
      case 'SelectEmployee':
        return <AnyEmployeeSlot onPickAnyEmployee={props.onPickAnyEmployee} />;
      case 'Search':
        return <SearchSlot onSearchTimes={props.onSearchTimes} />;
      case 'PickTime':
        return (
          <PickTimeSlot
            employeeName={props.employeeName}
            employeeImgUrl={props.employeeImgUrl}
            onChangeEmployee={props.onChangeEmployee}
          />
        );
      case 'PickTimeAnyStaffMember':
        return <AnyStaffSlot onChangeEmployee={props.onChangeEmployee} />;
      default: {
        const never: never = state;
        console.error('Invalid SelectionsBar state', never);
      }
    }
  })();

  /**
   * Every state but PickTime puts a fixed CTA on the right, which must keep its intrinsic
   * width. PickTime puts a merchant-supplied employee name there, so that slot is capped at
   * half the bar: the name ellipsizes inside its own half instead of overrunning the price.
   */
  const rightSlotClassName = state === 'PickTime' ? 'min-w-0 basis-1/2' : 'shrink-0';

  return (
    <div className="bg-bg_neutral pb-safe-xl w-full">
      <div className="border-border_regular px-md pt-md pb-sm gap-sm flex flex-row items-center border-t">
        {onEdit && <EditButton onEdit={onEdit} />}
        <LeftInfo serviceLabel={serviceLabel} price={price} originalPrice={originalPrice} />
        <div className={rightSlotClassName}>{rightSlot}</div>
      </div>
    </div>
  );
};

export default SelectionsBar;
